import { memo, useCallback, useMemo, useState } from "react";
import type { Car as CarType } from "@/types/database";
import { getPopularModelEntries, type PopularModelEntry } from "@/lib/analytics";
import { resolveCarMakeLogo, type CarMakeLogoEntry } from "@/lib/car-make-logos";
import { cn } from "@/lib/cn";
import { MakeLogo } from "@/components/cars/MakeModelGrid";
import { useCarMakeLogos } from "@/lib/queries";

const ORBIT_RADIUS = 50;
const ORBIT_RING_SIZE = ORBIT_RADIUS * 2 + 8;

type OrbitLogoCardProps = {
  entry: PopularModelEntry;
  logo: CarMakeLogoEntry | null;
  angle: number;
  isFocused: boolean;
  isDimmed: boolean;
  onFocus: () => void;
  onBlur: () => void;
};

const OrbitLogoCard = memo(function OrbitLogoCard({
  entry,
  logo,
  angle,
  isFocused,
  isDimmed,
  onFocus,
  onBlur,
}: OrbitLogoCardProps) {
  return (
    <div
      className="absolute left-0 top-0 h-0 w-0"
      style={{
        transform: `rotate(${angle}deg) translateY(-${ORBIT_RADIUS}px) rotate(-${angle}deg)`,
      }}
    >
      <div
        className={cn(
          "model-orbit-card flex flex-col items-center gap-1.5",
          isFocused && "is-focused z-20",
          isDimmed && "is-dimmed z-[1]",
          !isFocused && !isDimmed && "z-[5]"
        )}
        onMouseEnter={onFocus}
        onMouseLeave={onBlur}
      >
        <div
          className={cn(
            "relative flex h-10 w-10 items-center justify-center rounded-xl bg-background/90 shadow-[var(--shadow-elevation)]",
            isFocused && "shadow-[0_10px_28px_rgba(0,0,0,0.14)]"
          )}
        >
          <MakeLogo
            make={entry.make || entry.model}
            logo={logo}
            className="h-6 w-6 object-contain"
          />
          <span className="model-orbit-badge absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-0.5 text-[9px] font-bold text-accent-foreground">
            {entry.value}
          </span>
        </div>
        <div className="model-orbit-label pointer-events-none text-center">
          <p className="max-w-[88px] truncate text-[9px] font-bold leading-tight text-foreground">
            {entry.model}
          </p>
          {entry.make ? (
            <p className="mt-0.5 max-w-[88px] truncate text-[8px] font-semibold uppercase tracking-wide text-muted">
              {entry.make}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
});

export function FloatingModelLogos({ cars }: { cars: CarType[] }) {
  const { data: makeLogos = new Map<string, CarMakeLogoEntry>() } = useCarMakeLogos();
  const entries = useMemo(() => getPopularModelEntries(cars), [cars]);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  const items = useMemo(
    () =>
      entries.map((entry, index) => ({
        entry,
        logo: resolveCarMakeLogo(entry.make, makeLogos),
        angle: (index / entries.length) * 360,
      })),
    [entries, makeLogos]
  );

  const handleFocus = useCallback((index: number) => {
    setFocusedIndex(index);
  }, []);

  const handleBlur = useCallback(() => {
    setFocusedIndex(null);
  }, []);

  if (entries.length === 0) {
    return (
      <p className="flex h-full items-center justify-center text-sm text-muted">
        No model data to show.
      </p>
    );
  }

  return (
    <div className="grid h-full w-full place-items-center overflow-hidden">
      <div
        className="relative"
        style={{ width: ORBIT_RING_SIZE, height: ORBIT_RING_SIZE }}
      >
        <div className="pointer-events-none absolute inset-0 rounded-full border border-border/25 bg-background/20" />
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent/30" />

        <div className="absolute left-1/2 top-1/2 h-0 w-0">
          {items.map(({ entry, logo, angle }, index) => (
            <OrbitLogoCard
              key={entry.label}
              entry={entry}
              logo={logo}
              angle={angle}
              isFocused={focusedIndex === index}
              isDimmed={focusedIndex !== null && focusedIndex !== index}
              onFocus={() => handleFocus(index)}
              onBlur={handleBlur}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
